import { SCORING } from '@/config/audio';
import { templateBank, type ChordTemplate } from './templates';

export interface BlindDetection {
  /** Name of the best template, e.g. `Am`, `G7`. */
  name: string;
  score: number;
  /** Gap to the runner-up. Small gaps mean "could be either" — stay quiet. */
  margin: number;
  template: ChordTemplate;
}

/**
 * Blind chord identification by cosine similarity against binary templates
 * (Fujishima 1999).
 *
 * Cosine similarity handles the template-size problem for free: adding a note
 * that carries no energy grows the denominator without growing the numerator,
 * so a 4-note template does not automatically beat a triad.
 *
 * This is a *hint*, never a verdict. `bestDetection` returns null unless the
 * winner is both good in absolute terms and clearly ahead of the runner-up.
 */
export function rankTemplates(chroma: Float32Array): BlindDetection | null {
  let norm = 0;
  for (let i = 0; i < 12; i++) norm += chroma[i] * chroma[i];
  norm = Math.sqrt(norm);
  if (norm <= 0) return null;

  let best: ChordTemplate | null = null;
  let bestScore = -1;
  let secondScore = -1;

  for (const template of templateBank()) {
    let dot = 0;
    for (let i = 0; i < 12; i++) dot += chroma[i] * template.vector[i];
    const score = dot / norm;
    if (score > bestScore) {
      secondScore = bestScore;
      bestScore = score;
      best = template;
    } else if (score > secondScore) {
      secondScore = score;
    }
  }

  if (!best) return null;
  return {
    name: best.name,
    score: bestScore,
    margin: bestScore - Math.max(0, secondScore),
    template: best,
  };
}

/** Same as `rankTemplates`, but returns null when the result is not trustworthy. */
export function bestDetection(chroma: Float32Array): BlindDetection | null {
  const result = rankTemplates(chroma);
  if (!result) return null;
  if (result.score < SCORING.blindDetectionMinScore) return null;
  if (result.margin < SCORING.blindDetectionMargin) return null;
  return result;
}
